import { parseISO } from "date-fns/parseISO";
import { Static, String } from "runtypes";
import * as z from "zod";

import { notNullish } from "./filterGuards.js";

/** ISO date string */
export const DateString = String.withBrand("DateString");
export type DateString = Static<typeof DateString>;

export const getDateString = (date: Date): DateString =>
  date.toISOString().split("T")[0] as DateString;

// to improve this further, we could build a runtype constraint
// that check that this is a string of an expected datetime format
/** ISO datetime string */
export const DateTimeString = String.withBrand("DateTimeString");
export type DateTimeString = Static<typeof DateTimeString>;

export const getDateTimeString = (date: Date): DateTimeString =>
  date.toISOString() as DateTimeString;

/** {@link getDateTimeString}, passing `null` through. */
export const getNullableDateTimeString = (
  date: Date | null,
): DateTimeString | null => (date == null ? null : getDateTimeString(date));

// https://github.com/typeorm/typeorm/issues/2176
// typeorm returns date columns as strings. in-memory db unit tests return them as Date objects though. Most entities
// are also typed as Dates today so this method is intended to unify all this under DateTimeString
export const uniformDateTimeString = (date: Date | string): DateTimeString => {
  let dateObj: Date;

  if (typeof date === "object" && date instanceof Date) {
    dateObj = date;
  } else {
    const dateMs = Date.parse(date);
    if (Number.isNaN(dateMs)) {
      throw new Error("Unable to parse date");
    }

    dateObj = new Date(dateMs);
  }

  return getDateTimeString(dateObj);
};

/** {@link uniformDateTimeString}, passing `null` through. */
export const uniformNullableDateTimeString = (
  date: Date | string | null,
): DateTimeString | null => (date == null ? null : uniformDateTimeString(date));

export const TimezoneName = String.withBrand("TimezoneName");
export type TimezoneName = Static<typeof TimezoneName>;

export const minDateTimeString = (
  datetimeStrings: (DateTimeString | null | undefined)[],
): Date | null => {
  const dates = datetimeStrings
    .filter(notNullish)
    .map((d) => parseISO(d).valueOf());

  if (dates.length === 0) return null;
  return new Date(Math.min(...dates));
};

export const zDateTimeString = z.string().transform((val, ctx) => {
  try {
    return uniformDateTimeString(val);
  } catch {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: `Invalid date-time string: ${val}`,
    });
    return z.NEVER;
  }
});

export const zTimezoneName = z.string().transform((val) => val as TimezoneName);
